import { useState, useEffect } from 'react';
import { isCountryBlocked } from '../config/blockedCountries';

const GEO_CACHE_KEY = 'geo_country_code';
const GEO_CACHE_EXPIRY_KEY = 'geo_country_expiry';
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Custom hook for geo-blocking visitors from specific countries
 * Uses ipapi.co free tier (1000 requests/day) with sessionStorage caching
 *
 * @returns {Object} Geo-blocking state
 */
const useGeoBlock = () => {
  const [isBlocked, setIsBlocked] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [countryCode, setCountryCode] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkGeoLocation = async () => {
      try {
        // Check cache first
        const cachedCountry = sessionStorage.getItem(GEO_CACHE_KEY);
        const cacheExpiry = sessionStorage.getItem(GEO_CACHE_EXPIRY_KEY);

        if (cachedCountry && cacheExpiry && Date.now() < parseInt(cacheExpiry, 10)) {
          setCountryCode(cachedCountry);
          const blocked = isCountryBlocked(cachedCountry);
          setIsBlocked(blocked);
          if (blocked) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: 'geo_blocked',
              blockedCountry: cachedCountry
            });
          }
          setIsLoading(false);
          return;
        }

        // Fetch from geolocation API
        const response = await fetch('https://ipapi.co/json/', {
          headers: {
            'Accept': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Geo API returned ${response.status}`);
        }

        const data = await response.json();

        if (data.error) {
          // API returned an error (e.g., rate limited)
          throw new Error(data.reason || 'Geo API error');
        }

        const country = data.country_code;

        // Cache the result
        sessionStorage.setItem(GEO_CACHE_KEY, country);
        sessionStorage.setItem(GEO_CACHE_EXPIRY_KEY, String(Date.now() + CACHE_DURATION_MS));

        setCountryCode(country);
        const blocked = isCountryBlocked(country);
        setIsBlocked(blocked);

        if (blocked) {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'geo_blocked',
            blockedCountry: country
          });
        }
      } catch (err) {
        console.error('Geo-blocking check failed:', err);
        setError(err.message);
        // Fail open - if we can't determine location, allow access
        setIsBlocked(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkGeoLocation();
  }, []);

  return {
    isBlocked,
    isLoading,
    countryCode,
    error
  };
};

export default useGeoBlock;
